/**
 * Commission Override utilities.
 *
 * Allows setting a custom commission rate per listing.
 * Stored in listing.protectedData.commissionPercent (0-100).
 *
 * Usage in lineItems:
 *   import { getCommissionPercent, calculateCommission } from 'features/commissionOverride';
 *   const commissionPercent = getCommissionPercent(listing) ?? DEFAULT_COMMISSION_PERCENT;
 *   const commission = calculateCommission(subtotal, commissionPercent);
 */

/** Default fallback from env (e.g. 20 for 20%) */
export const DEFAULT_COMMISSION_PERCENT =
  parseFloat(process.env.REACT_APP_MARKETPLACE_COMMISSION_PERCENT || '20');

export const MIN_COMMISSION = 0;
export const MAX_COMMISSION = 100;

/**
 * Get custom commission percent from listing.
 * @param {Object|null} listing — Sharetribe listing object
 * @returns {number|null} — percent (0-100) or null if not set
 */
export const getCommissionPercent = (listing) => {
  const val = listing?.attributes?.protectedData?.commissionPercent;
  if (val === null || val === undefined) return null;
  const n = parseFloat(val);
  if (isNaN(n) || n < MIN_COMMISSION || n > MAX_COMMISSION) return null;
  return n;
};

/**
 * Calculate commission amount in cents.
 * @param {number} subtotalCents — subtotal in cents
 * @param {number} percent — commission percentage (0-100)
 * @returns {number} — commission in cents
 */
export const calculateCommission = (subtotalCents, percent) => {
  if (percent < 0 || percent > 100) throw new Error('Invalid commission percent');
  return Math.round(subtotalCents * (percent / 100));
};

/**
 * Validate a commission percent value.
 * @param {number|string} value
 * @returns {string|null} — error message or null if valid
 */
export const validateCommissionPercent = (value) => {
  const n = parseFloat(value);
  if (isNaN(n)) return 'Must be a number';
  if (n < MIN_COMMISSION) return `Must be at least ${MIN_COMMISSION}%`;
  if (n > MAX_COMMISSION) return `Must be at most ${MAX_COMMISSION}%`;
  return null;
};
